import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FileText, BookOpen, Plus, Edit2, Trash2, Eye, Calendar,
  Loader2, ArrowLeft, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';

const TECH_FONT = "'Inter', 'SF Pro Display', sans-serif";

const MyContentPage = () => {
  const navigate = useNavigate();
  const { user, isStaff, loading: authLoading } = useAuth();
  const [articles, setArticles] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('articles');

  useEffect(() => {
    if (authLoading) return;
    if (!isStaff) {
      navigate('/');
      return;
    }
    fetchContent();
  }, [isStaff, authLoading, navigate]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const [articlesData, blogData] = await Promise.all([
        api.getMyArticles(),
        api.getMyBlogPosts()
      ]);
      setArticles(articlesData || []);
      setBlogPosts(blogData || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlogPost = async (blogId) => {
    if (!window.confirm('¿Estás seguro de eliminar este post?')) return;
    try {
      await api.deleteBlogPost(blogId);
      fetchContent();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar post');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
      case 'active':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Publicado</Badge>;
      case 'draft':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Borrador</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-content-page">
      {/* Header */}
      <div className="bg-[#0f172a] text-white py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Link to="/" className="text-gray-400 hover:text-cyan-400">
                  <ArrowLeft className="w-5 h-5" />
                </Link>
                <h1 className="text-2xl font-bold" style={{ fontFamily: TECH_FONT }}>
                  Mi Contenido
                </h1>
              </div>
              <p className="text-gray-400 text-sm ml-8">
                Gestiona tus noticias y posts de blog
              </p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/admin/write')} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Noticia
              </Button>
              <Button onClick={() => navigate('/editor/blog/new')} variant="outline" className="border-cyan-500 text-cyan-500 hover:bg-cyan-500/10">
                <BookOpen className="w-4 h-4 mr-2" />
                Nuevo Post
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="articles" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Mis Noticias ({articles.length})
            </TabsTrigger>
            <TabsTrigger value="blog" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Mis Posts ({blogPosts.length})
            </TabsTrigger>
          </TabsList>

          {/* Articles Tab */}
          <TabsContent value="articles">
            {articles.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-600 mb-2">No tienes noticias</h3>
                <p className="text-gray-400 mb-6">Crea tu primera noticia con asistencia de IA</p>
                <Button onClick={() => navigate('/admin/write')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Noticia
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Título</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Categoría</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {articles.map((article) => (
                      <tr key={article.dossier_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {article.cover_image && (
                              <img src={article.cover_image?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${article.cover_image}` : article.cover_image} alt="" className="w-12 h-12 rounded object-cover" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900 line-clamp-1">{article.title}</p>
                              <p className="text-sm text-gray-500 line-clamp-1">{article.summary?.slice(0, 60)}...</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant="outline">{article.category || 'General'}</Badge>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(article.status)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {article.created_at ? new Date(article.created_at).toLocaleDateString('es') : '—'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/noticias/${article.dossier_id}`}>
                              <Button variant="ghost" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/write?edit=${article.dossier_id}`)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            {blogPosts.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-600 mb-2">No tienes posts de blog</h3>
                <p className="text-gray-400 mb-6">Comparte tus opiniones y análisis con la comunidad</p>
                <Button onClick={() => navigate('/editor/blog/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Post
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Título</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Categoría</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Vistas</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {blogPosts.map((post) => (
                      <tr key={post.blog_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900 line-clamp-1">{post.title}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{post.excerpt?.slice(0, 60)}...</p>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant="outline">{post.category || 'General'}</Badge>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(post.status)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {post.views || 0}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {post.created_at ? new Date(post.created_at).toLocaleDateString('es') : '—'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {post.status === 'published' && (
                              <Link to={`/blog/${post.blog_id}`}>
                                <Button variant="ghost" size="sm">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/editor/blog/${post.blog_id}`)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDeleteBlogPost(post.blog_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MyContentPage;
